'use client';

import { useState } from 'react';
import Link from 'next/link';
import LazyVideo from './LazyVideo';

const services = [
  {
    id: 'stampa-3d',
    title: 'Stampa 3D',
    shortTitle: 'Stampa 3D',
    description: 'Realizziamo prototipi e produzioni in serie con tecnologie FDM, SLA e SLS. Ampia scelta di materiali plastici e resine per ogni esigenza.',
    features: ['Prototipazione rapida', 'Produzione in serie', 'Multi-materiale'],
    href: '/servizi/stampa-3d',
    media: '/images/placeholder-stampa3d.jpg',
    video: '/videos/stampa-v2.mp4',
    poster: '/videos/stampa-poster.webp',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
      </svg>
    ),
    gradient: 'from-primary-500 to-primary-700',
    bgPattern: (
      <svg className="absolute inset-0 w-full h-full opacity-[0.03]" viewBox="0 0 100 100">
        <pattern id="grid-stampa" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
        </pattern>
        <rect width="100" height="100" fill="url(#grid-stampa)"/>
        {/* Layer lines pattern */}
        {[...Array(12)].map((_, i) => (
          <line key={i} x1="0" y1={8 + i * 7} x2="100" y2={8 + i * 7} stroke="currentColor" strokeWidth="0.3" opacity="0.5"/>
        ))}
      </svg>
    ),
  },
  {
    id: 'scansione-3d',
    title: 'Scansione 3D',
    shortTitle: 'Scansione 3D',
    description: 'Digitalizziamo oggetti fisici e creiamo modelli 3D editabili. Ideale per replicare pezzi esistenti o analizzare geometrie complesse.',
    features: ['Reverse engineering', 'Alta precisione', 'Modelli editabili'],
    href: '/servizi/scansione-3d',
    media: '/images/placeholder-scansione.jpg',
    video: '/videos/scansione-v2.mp4',
    poster: '/videos/scansione-poster.webp',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8.25v7.5m3.75-3.75h-7.5" />
      </svg>
    ),
    gradient: 'from-cyan-500 to-blue-600',
    bgPattern: (
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 100 100">
        {/* Scanning lines effect */}
        {[...Array(20)].map((_, i) => (
          <line key={i} x1="0" y1={5 * i} x2="100" y2={5 * i} stroke="currentColor" strokeWidth="0.2" strokeDasharray="2 4"/>
        ))}
        {/* Center target */}
        <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.3"/>
        <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="0.3"/>
        <circle cx="50" cy="50" r="10" fill="none" stroke="currentColor" strokeWidth="0.3"/>
      </svg>
    ),
  },
  {
    id: 'progettazione-cad',
    title: 'Progettazione CAD',
    shortTitle: 'Progettazione',
    description: 'Progettiamo da zero il tuo modello 3D partendo da schizzi, disegni tecnici o semplici idee. Supporto completo dalla concept alla produzione.',
    features: ['Design parametrico', 'Da idea a prototipo', 'File pronti per la stampa'],
    href: '/servizi/progettazione-cad',
    media: '/images/placeholder-cad.jpg',
    video: '/videos/cad-v2.mp4',
    poster: '/videos/cad-poster.webp',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
      </svg>
    ),
    gradient: 'from-violet-500 to-purple-700',
    bgPattern: (
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 100 100">
        {/* Blueprint grid */}
        <pattern id="blueprint" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.3"/>
          <path d="M 10 0 L 10 20 M 0 10 L 20 10" fill="none" stroke="currentColor" strokeWidth="0.15"/>
        </pattern>
        <rect width="100" height="100" fill="url(#blueprint)"/>
        {/* 3D cube wireframe */}
        <g transform="translate(50,50)" stroke="currentColor" strokeWidth="0.4" fill="none">
          <polygon points="-15,-10 0,-20 15,-10 0,0"/>
          <polygon points="-15,-10 -15,10 0,20 0,0"/>
          <polygon points="15,-10 15,10 0,20 0,0"/>
        </g>
      </svg>
    ),
  },
];

export default function Services() {
  const [activeService, setActiveService] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleServiceChange = (index: number) => {
    if (index === activeService) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveService(index);
      setIsTransitioning(false);
    }, 150);
  };

  const current = services[activeService];

  return (
    <section id="servizi" className="py-24 bg-gradient-to-b from-white to-secondary-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="section-title font-display">
            I nostri <span className="text-gradient">servizi</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Offriamo soluzioni complete per la manifattura additiva e la progettazione 3D.
          </p>
        </div>

        {/* Interactive service selector */}
        <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          
          {/* Left: Service tabs */}
          <div className="flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => handleServiceChange(index)}
                className={`
                  group relative flex items-center gap-4 p-4 lg:p-5 rounded-xl lg:rounded-2xl
                  transition-all duration-300 text-left min-w-[200px] lg:min-w-0
                  ${activeService === index 
                    ? 'bg-white shadow-xl shadow-primary-500/10 border-2 border-primary-500/20' 
                    : 'bg-white/50 hover:bg-white border-2 border-transparent hover:border-secondary-100'
                  }
                `}
              >
                {/* Active indicator line */}
                <div className={`
                  absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full
                  transition-all duration-300 hidden lg:block
                  ${activeService === index ? 'bg-primary-500' : 'bg-transparent'}
                `} />
                
                {/* Icon */}
                <div className={`
                  flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
                  transition-all duration-300
                  ${activeService === index 
                    ? `bg-gradient-to-br ${service.gradient} text-white shadow-lg` 
                    : 'bg-secondary-100 text-secondary-400 group-hover:bg-secondary-200'
                  }
                `}>
                  {service.icon}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <h3 className={`
                    font-semibold transition-colors duration-300 truncate
                    ${activeService === index ? 'text-primary-600' : 'text-secondary-500 group-hover:text-secondary-600'}
                  `}>
                    <span className="hidden lg:inline">{service.title}</span>
                    <span className="lg:hidden">{service.shortTitle}</span>
                  </h3>
                  <p className={`
                    text-sm transition-colors duration-300 hidden lg:block
                    ${activeService === index ? 'text-secondary-500' : 'text-secondary-400'}
                  `}>
                    Clicca per esplorare
                  </p>
                </div>

                {/* Arrow indicator */}
                <svg 
                  className={`
                    w-5 h-5 flex-shrink-0 transition-all duration-300 hidden lg:block
                    ${activeService === index 
                      ? 'text-primary-600 translate-x-1' 
                      : 'text-secondary-400 group-hover:text-secondary-400'
                    }
                  `}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>

          {/* Right: Service content */}
          <div 
            className={`
              relative rounded-3xl overflow-hidden
              shadow-2xl shadow-secondary-500/10 border border-secondary-100
              transition-opacity duration-150
              ${isTransitioning ? 'opacity-0' : 'opacity-100'}
            `}
          >
            <div className="flex flex-col lg:flex-row">
              {/* Left: Media slot - più grande e integrato */}
              <div className="relative lg:w-1/2 aspect-video lg:aspect-auto lg:min-h-[420px] bg-secondary-900 overflow-hidden">
                {/* Video background se disponibile */}
                {current.video ? (
                  <LazyVideo
                    key={current.video}
                    src={current.video}
                    poster={current.poster}
                    lazyPoster
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <>
                    {/* Gradient overlay per placeholder */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${current.gradient} opacity-60`} />
                    
                    {/* Pattern decorativo */}
                    <div className="absolute inset-0 text-white/10">
                      {current.bgPattern}
                    </div>

                    {/* Play button overlay per placeholder */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className={`
                        w-20 h-20 rounded-full bg-white/95 shadow-2xl
                        flex items-center justify-center
                      `}>
                        <svg className="w-8 h-8 text-secondary-700 ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <span className="mt-4 text-white/80 text-sm font-medium">Video in arrivo</span>
                    </div>
                  </>
                )}

                </div>

              {/* Right: Text content */}
              <div className="relative lg:w-1/2 bg-white p-8 lg:p-10">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`
                    w-14 h-14 rounded-2xl flex items-center justify-center
                    bg-gradient-to-br ${current.gradient} text-white shadow-lg
                  `}>
                    {current.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-secondary-500 font-display">
                      {current.title}
                    </h3>
                    <p className="text-secondary-400 text-sm mt-1">
                      Servizio professionale
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-secondary-500 text-lg leading-relaxed mb-6">
                  {current.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {current.features.map((feature) => (
                    <span 
                      key={feature}
                      className={`
                        inline-flex items-center gap-2 px-4 py-2 rounded-full
                        bg-gradient-to-r ${current.gradient}
                        text-sm font-medium text-white
                        shadow-md
                      `}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <Link 
                  href={current.href}
                  className={`
                    inline-flex items-center gap-3 px-6 py-3.5 rounded-xl
                    bg-gradient-to-r ${current.gradient} text-white font-semibold
                    hover:shadow-lg hover:shadow-primary-500/25 hover:-translate-y-0.5
                    transition-all duration-300
                  `}
                >
                  Scopri {current.shortTitle}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-secondary-400 mb-4">
            Non sai quale servizio fa per te?
          </p>
          <Link 
            href="/servizi"
            className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
          >
            Esplora tutti i servizi
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

